import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { GridLayout } from '../lib/api';
import { clientId } from '../lib/clientId';

/**
 * Tiga peran §F8. Nilai `viewer` dipertahankan apa adanya dari DB walaupun
 * labelnya di layar "User" — 43 akun hasil import memakainya.
 */
export type UserRole = 'admin' | 'operator' | 'viewer';

/** Boleh mengubah isi Lensa: tambah kamera, sunting, susun dashboard (§F8). */
export function isStaff(user: { role: UserRole; viewAsUser: boolean } | null): boolean {
  if (!user || user.viewAsUser) return false;
  return user.role === 'admin' || user.role === 'operator';
}

/**
 * Boleh melakukan yang tidak bisa dibatalkan: hapus kamera, hapus dashboard,
 * kelola akun, ubah setting sistem (§F8).
 *
 * User view menurunkan keduanya — dan itu memang gunanya. Pemeriksaan yang
 * sebenarnya tetap di server; yang di sini hanya supaya tombolnya tidak
 * ditawarkan lalu ditolak.
 */
export function isAdmin(user: { role: UserRole; viewAsUser: boolean } | null): boolean {
  return !!user && !user.viewAsUser && user.role === 'admin';
}

/** Boleh menetapkan/melepas pemilik kamera privat. Wajib staf; server yang menentukan. */
export function canManagePrivate(
  user: { role: UserRole; viewAsUser: boolean; canManagePrivate?: boolean } | null,
): boolean {
  return isStaff(user) && !!user?.canManagePrivate;
}

export interface User {
  id: string;
  username: string;
  email: string | null;
  fullName: string;
  phone: string | null;
  address: string | null;
  /** Ada foto profil atau tidak. Byte-nya diambil terpisah lewat `avatarUrl`. */
  hasAvatar: boolean;
  /**
   * Sidik isi avatar — ikut ke dalam URL gambarnya supaya foto yang baru
   * diganti langsung terlihat di semua tempat, bukan cuma di kartu tempat
   * menggantinya (§F7).
   */
  avatarVersion: string | null;
  /** `ldap` = password dikelola direktori, tidak bisa diganti dari sini. */
  authSource: 'local' | 'ldap';
  role: UserRole;
  /**
   * Sesi ini sedang dalam **User view** (§F8.1): chrome-nya jadi seperti milik
   * pemakai biasa dan seluruh wewenang mengubah dicabut — di server juga, bukan
   * hanya disembunyikan di sini.
   *
   * Yang **tidak** ikut turun adalah kamera yang terlihat: admin memang melihat
   * semua kamera tanpa perlu dashboard.
   */
  viewAsUser: boolean;
  /** Pengelola kamera privat. Dianggap `false` saat `viewAsUser` — lewat `canManagePrivate()`. */
  canManagePrivate: boolean;
  theme: 'light' | 'dark' | 'system';
  /** `null` = belum pernah memilih; dashboard memakai default lebar layar. */
  gridLayout: GridLayout | null;
}

/** Identitas asli di balik sesi yang sedang meminjam akun orang lain. */
export interface Impersonator {
  id: string;
  username: string;
  fullName: string;
}

interface AuthState {
  user: User | null;
  /** Terisi hanya saat sesi ini sedang meminjam akun orang lain. */
  impersonator: Impersonator | null;
  /** `true` selama pemeriksaan sesi awal — bedakan dari "sudah pasti anonim". */
  loading: boolean;
  login: (identifier: string, password: string, remember: boolean) => Promise<void>;
  logout: () => Promise<void>;
  /** Simpan pilihan tampilan ke akun, bukan ke browser (§G8). */
  savePrefs: (prefs: Partial<Pick<User, 'theme' | 'gridLayout'>>) => void;
  /**
   * Tarik ulang identitas dari server.
   *
   * Dipakai setelah profil disunting dan setelah pindah akun — keduanya
   * mengubah siapa yang tampil di header, dan menebaknya di klien akan membuat
   * header dan isi halaman sempat menampilkan dua orang yang berbeda.
   */
  refresh: () => Promise<void>;
  /**
   * Masuk/keluar User view (§F8.1).
   *
   * Ditunggu, tidak seperti `savePrefs`: yang berubah wewenang, dan UI yang
   * lebih dulu berubah daripada servernya akan menawarkan tombol yang langsung
   * ditolak — atau lebih buruk, menyembunyikan tombol yang sebenarnya masih
   * hidup.
   */
  setViewMode: (userView: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Dilempar saat login ditolak, membawa pesan apa adanya dari server (§F1). */
export class LoginError extends Error {
  constructor(
    message: string,
    readonly retryAfter?: number,
  ) {
    super(message);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [impersonator, setImpersonator] = useState<Impersonator | null>(null);
  const [loading, setLoading] = useState(true);

  // Sekali di awal: tanyakan apakah sudah ada sesi. Ini juga yang memicu
  // pemulihan lewat cookie "Ingat saya" di sisi server (§F4.2).
  useEffect(() => {
    let alive = true;
    fetch('/api/v1/auth/me')
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!alive) return;
        setUser(body?.user ?? null);
        setImpersonator(body?.impersonator ?? null);
      })
      .catch(() => {
        if (alive) setUser(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback(async (identifier: string, password: string, remember: boolean) => {
    const res = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier, password, remember }),
    });
    const body = await res.json().catch(() => null);

    if (!res.ok) {
      const retryAfter = Number(res.headers.get('Retry-After')) || undefined;
      throw new LoginError(body?.message ?? 'Could not sign in. Please try again.', retryAfter);
    }
    setUser(body.user as User);
    // Login selalu mendarat di akun sendiri — sisa keadaan pinjaman dari sesi
    // sebelumnya di tab ini tidak boleh ikut terbawa.
    setImpersonator(null);
  }, []);

  const refresh = useCallback(async () => {
    const res = await fetch('/api/v1/auth/me');
    if (!res.ok) {
      setUser(null);
      setImpersonator(null);
      return;
    }
    const body = await res.json().catch(() => null);
    setUser(body?.user ?? null);
    setImpersonator(body?.impersonator ?? null);
  }, []);

  /**
   * Menyimpan preferensi sengaja **tidak** ditunggu dan kegagalannya tidak
   * dinaikkan ke UI: yang diminta orang adalah tata letaknya berubah sekarang,
   * dan itu sudah terjadi di state lokal. Kalau simpanannya gagal, yang hilang
   * cuma ingatannya untuk login berikutnya — bukan alasan menyodorkan pesan
   * galat di atas gambar kamera.
   */
  const savePrefs = useCallback((prefs: Partial<Pick<User, 'theme' | 'gridLayout'>>) => {
    setUser((u) => (u ? { ...u, ...prefs } : u));
    void fetch('/api/v1/auth/me', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prefs),
    }).catch(() => undefined);
  }, []);

  const setViewMode = useCallback(async (userView: boolean) => {
    const res = await fetch('/api/v1/auth/view-mode', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userView }),
    });
    if (!res.ok) return;
    const body = await res.json().catch(() => null);
    if (body?.user) setUser(body.user as User);
  }, []);

  const logout = useCallback(async () => {
    // Badan `{}` + Content-Type wajib ada, sama alasannya dengan `api()`:
    // POST tanpa badan dijawab 415 begitu lewat Zoraxy, dan `.catch()`
    // di bawah menelan kegagalannya bulat-bulat — UI tampak logout sementara
    // sesinya masih hidup di server.
    await fetch('/api/v1/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // `clientId` ikut supaya server bisa langsung memutus aliran MJPEG milik
      // tab ini (§D3.4). Hak akses aliran diperiksa sekali saat dibuka lalu
      // hidup berjam-jam — tanpa ini, gambar tetap mengalir ke orang yang baru
      // saja menekan Logout.
      body: JSON.stringify({ clientId: clientId() }),
    }).catch(() => undefined);
    setUser(null);
    setImpersonator(null);
  }, []);

  const value = useMemo(
    () => ({ user, impersonator, loading, login, logout, savePrefs, refresh, setViewMode }),
    [user, impersonator, loading, login, logout, savePrefs, refresh, setViewMode],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
